/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import type { ResultTaskList } from "client";
import { PointSource } from "components/Tasks/SelectPointPanel";
import { ChargerItem, useChargers } from "hooks/GetChargersHook";
import { MaterialItem, useMaterials } from "hooks/GetMaterialsHook";
import { usePointNodes } from "hooks/GetPointNodesHook";
import { UserInfo, useUsers } from "hooks/GetUsersHook";
import {
  useContext,
  createContext,
  PropsWithChildren,
  FC,
  useState,
  useEffect,
} from "react";

// context data task Register / edit
export interface TaskData extends ResultTaskList {
  srcJobSetId?: string;
}

interface TaskRegisterDataContext {
  ready: boolean;
  errors: Error[];
  pointNodes: PointSource[];
  materials?: MaterialItem[];
  chargers?: ChargerItem[];
  users?: UserInfo[];
}

export const initializeTaskData: TaskData = {};
const defaultValue: TaskRegisterDataContext = {
  ready: false,
  errors: [],
  pointNodes: [],
  users: [],
};

const context = createContext<TaskRegisterDataContext>(defaultValue);

interface Props {
  value?: TaskData;
}

export const TaskRegisterDataProvider: FC<PropsWithChildren<Props>> = ({
  children,
}) => {
  const [ready, setReady] = useState(false);
  const [errors, setErrors] = useState<Error[]>([]);
  const pointNodes: PointSource[] = usePointNodes();
  const materials = useMaterials();
  const users = useUsers();
  const chargers = useChargers();
  useEffect(() => {
    setReady(!materials.loading && !chargers.loading && !users.loading);
  }, [chargers.loading, materials.loading, users.loading]);
  useEffect(() => {
    if (materials.error !== undefined) {
      setErrors((current) => [...current, materials.error!]);
    }
    if (chargers.error !== undefined) {
      setErrors((current) => [...current, chargers.error!]);
    }
    if (users.error !== undefined) {
      setErrors((current) => [...current, users.error!]);
    }
  }, [chargers.error, materials.error, users.error]);

  return (
    <context.Provider
      value={{
        ready,
        errors,
        pointNodes,
        materials: materials.value,
        users: users.value,
        chargers: chargers.value,
      }}
    >
      {children}
    </context.Provider>
  );
};

export const useTaskRegisterDataContext = () => {
  return useContext(context);
};
